<script lang="ts" setup>
import { onBeforeMount, ref } from "vue";
import AppFooter from "@/components/organisms/common/AppFooter.vue";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FavoriteButton from "@/components/organisms/common/FavoriteButton.vue";
import FavListNav from "@/components/organisms/mypage/FavListNav.vue";
import Header from "@/components/organisms/common/Header.vue";
import { accountService } from "@/app/services/AccountService";
import { favoriteService } from "@/app/services/FavoriteService";
import { inAppReviewService } from "@/app/services/InAppReviewService";
import { FavoriteOffice } from "@/types/office/FavoriteOffice";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";
import { useToast } from "@/utils/toast";
import { parseISO } from "@/utils/date";
import OfficeCard from "@/components/organisms/office/OfficeCard.vue";

const loader = useLoader();
const errorModal = useErrorModal();
const toast = useToast();

const favoriteOffices = ref<FavoriteOffice[]>([]);
const initialized = ref(false);

onBeforeMount(async () => {
  try {
    loader.show();
    // アカウント情報を更新
    await accountService.updateUserData();
    await fetchFavoriteOffices();
  } catch (error) {
    await errorModal.showErrorModal(error as Error);
  } finally {
    initialized.value = true;
    loader.hide();
  }
});

// お気に入りアイコン押下時
async function onClickFavoriteIcon(index: number) {
  const favorite = favoriteOffices.value[index];
  try {
    loader.show();
    const newFavorite = await favoriteService.updateFavoriteOffices(
      favorite.id,
      {
        office: favorite.office.id,
        isFavorite: !favorite.isFavorite
      }
    );
    const newFavoriteOffices = favoriteOffices.value.slice();
    newFavoriteOffices.splice(index, 1, newFavorite);
    favoriteOffices.value = newFavoriteOffices;

    toast.toast(
      newFavorite.isFavorite
        ? "事業所のお気に入りに追加しました"
        : "事業所のお気に入りを削除しました",
      {
        toastClass: ["toast-custom", "toast-custom-thin-header-page"],
        toaster: "b-toaster-top-full",
        autoHideDelay: 2000
      }
    );
    if (newFavorite.isFavorite) {
      await inAppReviewService.launchInAppReview("FAVORITE_OFFICE");
    }
  } catch (e) {
    await errorModal.showErrorModal(e as Error);
  } finally {
    loader.hide();
  }
}

async function fetchFavoriteOffices() {
  const response = await accountService.fetchFavoriteOffices({
    // NOTE: 下記の問題への暫定対応として、limitを400に設定
    // https://caitech.slack.com/archives/C037TMRCQDP/p1683792027718569
    limit: 400
  });
  // お気に入り登録降順(updatedAt降順)にソートして代入
  favoriteOffices.value = response.results.sort((a, b) => {
    return parseISO(b.updatedAt) - parseISO(a.updatedAt);
  });
}
</script>

<template>
  <div>
    <Header>
      お気に入り
    </Header>
    <FavListNav />
    <template v-if="initialized">
      <template v-if="favoriteOffices.length !== 0">
        <div class="mt-3 mx-2">
          <div
            v-for="(favoriteOffice, index) in favoriteOffices"
            :key="'favorite-office' + favoriteOffice.id"
            :ref="'favoriteOffice' + favoriteOffice.id"
            class="bg-white mb-2"
          >
            <OfficeCard :office="favoriteOffice.office">
              <FavoriteButton
                :isFavorite="favoriteOffice.isFavorite"
                @click="onClickFavoriteIcon(index)"
              />
            </OfficeCard>
          </div>
        </div>
      </template>
      <template v-if="favoriteOffices.length === 0">
        <div class="px-3 py-3">
          <div class="text-center">
            <Balloon />
          </div>
          <p class="text-size-14 text-mild pt-4">
            お気に入り事業所を登録すると、事業所がお仕事を募集した際に通知が届きます<br />
            お仕事を探して気になった事業所をお気に入り登録してみましょう
          </p>
          <AppButton block class="my-4" :to="{ name: 'Search' }">
            お気に入り事業所を探す
          </AppButton>
        </div>
      </template>
    </template>
    <AppFooter />
    <BlockBack />
  </div>
</template>
